import { useEffectOnce } from '@pum-spark/react-hooks';
import { isValid } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import shallow from 'zustand/shallow';
import { DateResolution } from '../../api/generated/data-contracts';
import { ITimeline } from '../../context/contexts/timelineContext';
import { useStore } from '../../context/store';
import { getToday, getDefaultForResolution, constrainDate, addPeriod } from './helpers';
import { useUISettings } from '../../context/network/http/QueryProvider/queries/UISettings';
import { useMinMaxDates } from './hooks/useMinMaxDates';

export const useTimeline = () => {
    const { data: uiSettings } = useUISettings();
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const { currentResolution, start, end, _setResolution, _setTimeline, _setTimelineEnd, _setTimelineStart } = useStore(
        store => ({
            currentResolution: store.timeline.resolution,
            _setResolution: store.setTimelineResolution,
            _setTimeline: store.setTimeline,
            _setTimelineEnd: store.setTimelineEnd,
            _setTimelineStart: store.setTimelineStart,
            start: store.timeline.start,
            end: store.timeline.end,
        }),
        shallow,
    );

    const setResolution = useCallback(
        (resolution: DateResolution) => {
            _setResolution(resolution);
            setUrlSearchParams(prev => {
                const searchParams = new URLSearchParams(prev);
                searchParams.set('resolution', resolution.toString());
                searchParams.set('start', start.toISOString());
                searchParams.set('end', end.toISOString());
                return searchParams;
            });
        },
        [_setResolution, end, setUrlSearchParams, start],
    );

    const setTimeline = useCallback(
        (timeline: Partial<ITimeline>) => {
            _setTimeline(timeline);
            setUrlSearchParams(prev => {
                const searchParams = new URLSearchParams(prev);
                searchParams.set('resolution', (timeline.resolution ?? currentResolution).toString());
                searchParams.set('start', (timeline.start ?? start).toISOString());
                searchParams.set('end', (timeline.end ?? end).toISOString());
                return searchParams;
            });
        },
        [_setTimeline, currentResolution, end, setUrlSearchParams, start],
    );

    const setTimelineEnd = useCallback(
        (end: Date) => {
            _setTimelineEnd(end);
            setUrlSearchParams(prev => {
                const searchParams = new URLSearchParams(prev);
                searchParams.set('resolution', currentResolution.toString());
                searchParams.set('start', start.toISOString());
                searchParams.set('end', end.toISOString());
                return searchParams;
            });
        },
        [_setTimelineEnd, currentResolution, setUrlSearchParams, start],
    );

    const setTimelineStart = useCallback(
        (start: Date) => {
            _setTimelineStart(start);
            setUrlSearchParams(prev => {
                const searchParams = new URLSearchParams(prev);
                searchParams.set('resolution', currentResolution.toString());
                searchParams.set('start', start.toISOString());
                searchParams.set('end', end.toISOString());
                return searchParams;
            });
        },
        [_setTimelineStart, currentResolution, end, setUrlSearchParams],
    );

    const getMinMax = useMinMaxDates();
	const [minDate, maxDate] = useMemo(() => getMinMax(currentResolution), [currentResolution, getMinMax])

    useEffectOnce(() => {
        const resolutionString = urlSearchParams.get('resolution');
        const startString = urlSearchParams.get('start');
        const endString = urlSearchParams.get('end');
        let resolution = currentResolution;
		const defaultPeriodPickerResolution = uiSettings.settings.periodPickerSettings.defaultPeriodPickerResolution
        if (resolutionString) {
            const resolution = Number(resolutionString);
            if (!Number.isNaN(resolution)) {
                _setResolution(resolution as DateResolution);
            }
        } else if (resolution === null) {
            resolution = defaultPeriodPickerResolution;
            _setResolution(resolution);
        }
        const today = getToday();
        if (startString && isValid(startString)) {
            _setTimelineStart(new Date(startString));
        } else if (start === null) {
            _setTimelineStart(today);
        }

        if (endString && isValid(endString)) {
            _setTimelineEnd(new Date(endString));
        } else if (end === null) {
            const { defaultSelectedUnits } = getDefaultForResolution(resolution, uiSettings.settings.periodPickerSettings);
            _setTimelineEnd(addPeriod(resolution, constrainDate(getToday(), minDate, maxDate), defaultSelectedUnits));
        }
    });

	useEffectOnce(() => {
		// If the allowed resolutions has changed, and the current resolution is not allowed, set the resolution to the default resolution
		if (!uiSettings.settings.allowedTimelineResolutions.includes(currentResolution)) {
			setResolution(uiSettings.settings.periodPickerSettings.defaultPeriodPickerResolution);
		}
	})

    return {
        currentResolution,
        start,
        end,
        setResolution,
        setTimeline,
        setTimelineEnd,
        setTimelineStart,
    };
};
